import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "../PageHeader";
import useHouseholdUsers from "../../hooks/useHouseholdUsers";
import { discussionApi } from "../../services/api";
import { parseApiError } from "../../services/apiError";
import { userLabel } from "../tasks/userDisplay";

const MAX_PANELISTS = 8;

/**
 * Start a discussion (projects/discussions D3, D13): a title, an opening
 * post, and — optionally — a panel whose answers stay hidden from each
 * other until everyone has answered.
 */
function NewDiscussion() {
  const navigate = useNavigate();
  const { me } = useOutletContext() || {};
  const {
    users,
    loading: usersLoading,
    error: usersError,
    refresh: reloadUsers,
  } = useHouseholdUsers();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [asPanel, setAsPanel] = useState(false);
  const [panelists, setPanelists] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);

  const candidates = users.filter((u) => u.id !== me?.id);
  const full = panelists.length >= MAX_PANELISTS;

  const toggle = (name, checked) =>
    setPanelists((prev) =>
      checked ? [...prev, name] : prev.filter((n) => n !== name),
    );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const payload = { title, body };
      if (asPanel && panelists.length) payload.panelists = panelists;
      const { data } = await discussionApi.create(payload);
      navigate(`/discussions/${data.id}`);
    } catch (err) {
      const parsed = parseApiError(err, "Couldn't start the discussion.");
      setFieldErrors(parsed.fieldErrors);
      setFormError(parsed.formError);
      setSaving(false);
    }
  };

  const fieldError = (name) =>
    fieldErrors[name] ? (
      <p className="m-0 text-sm text-destructive" id={`new-${name}-error`}>
        {[].concat(fieldErrors[name]).join(" ")}
      </p>
    ) : null;

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: 880 }}>
      <PageHeader
        title="Start a discussion"
        subtitle="Everyone in your household — people and their agents — can read and reply."
      />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor="new-title">Title</Label>
          <Input
            id="new-title"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What should Vision Quest sound like?"
            aria-invalid={Boolean(fieldErrors.title)}
            aria-describedby={fieldErrors.title ? "new-title-error" : undefined}
          />
          {fieldError("title")}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="new-body">Opening post</Label>
          <Textarea
            id="new-body"
            rows={6}
            value={body}
            maxLength={10000}
            onChange={(e) => setBody(e.target.value)}
            className="max-h-48"
            aria-invalid={Boolean(fieldErrors.body)}
            aria-describedby={
              fieldErrors.body ? "new-body-hint new-body-error" : "new-body-hint"
            }
          />
          <p id="new-body-hint" className="m-0 text-sm text-muted-foreground">
            Markdown works. Mention someone with @name — it shows up under “For
            you” for them.
          </p>
          {fieldError("body")}
        </div>

        <div className="grid gap-2 rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="new-panel"
              checked={asPanel}
              onCheckedChange={(v) => setAsPanel(v === true)}
            />
            <Label htmlFor="new-panel">Ask a panel</Label>
          </div>
          <p className="m-0 text-sm text-muted-foreground">
            Each panelist answers without seeing the others&apos; answers. They
            open when everyone has answered — or when you open them. Good for
            independent views instead of agreement with whoever spoke first.
          </p>
          {asPanel && (
            <fieldset
              className="m-0 border-0 p-0"
              aria-describedby="new-panel-limit"
            >
              <legend className="mb-1 text-sm font-medium">
                Panelists ({panelists.length} of up to {MAX_PANELISTS})
              </legend>
              {usersLoading ? (
                <p className="m-0 text-sm text-muted-foreground" role="status">
                  Loading your household…
                </p>
              ) : usersError ? (
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-destructive">
                    Couldn&apos;t load your household, so there&apos;s no one
                    to pick yet.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={reloadUsers}
                  >
                    Retry
                  </Button>
                </div>
              ) : candidates.length === 0 ? (
                <p className="m-0 text-sm text-muted-foreground">
                  There&apos;s no one else in your household to ask yet — a
                  panel needs at least one other person or agent.
                </p>
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {candidates.map((u) => {
                    const checked = panelists.includes(u.username);
                    return (
                      <div key={u.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`panelist-${u.id}`}
                          checked={checked}
                          disabled={!checked && full}
                          onCheckedChange={(v) =>
                            toggle(u.username, v === true)
                          }
                        />
                        <Label
                          htmlFor={`panelist-${u.id}`}
                          className="font-normal"
                        >
                          {userLabel(u)}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              )}
              <p
                id="new-panel-limit"
                className="m-0 mt-1 text-sm text-muted-foreground"
              >
                {full ? "That's the most a panel can have." : " "}
              </p>
              {fieldError("panelists")}
            </fieldset>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            type="submit"
            className="h-11 md:h-9"
            disabled={saving || (asPanel && panelists.length === 0)}
          >
            {saving && <Loader2 className="animate-spin" />}
            {asPanel ? "Ask the panel" : "Start"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 md:h-9"
            onClick={() => navigate("/discussions")}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NewDiscussion;
